/**
 * Load apps data from JSON file exported from MongoDB
 * This allows us to have real data for hardcoded apps without API calls
 *
 * Field names match the MongoDB /seo collection:
 * - app: app slug/identifier
 * - name: display name
 * - icon: icon URL
 * - description: app description
 * - category: app category
 * - categoryTitle: display category label
 * - group: optional group name
 * - formDataEndpoint: Lambda API endpoint
 * - publishStatus / status: publication state
 *
 * To update: Export from MongoDB and save as src/data/seo.json
 */

// Import the apps data (exported directly from MongoDB)
// File is named seo.json to match the MongoDB collection name
import appsData from '../data/seo.json';

/** Base URL used when formDataEndpoint is missing or empty in seo.json */
export const FORMDATA_ENDPOINT_FALLBACK_BASE =
  'https://odkusrnpx8.execute-api.us-west-2.amazonaws.com/production/apps';

/**
 * Get the formDataEndpoint with fallback when missing or empty.
 * Blank/missing → https://odkusrnpx8.execute-api.us-west-2.amazonaws.com/production/apps/{slug}
 * @param {Object} app - App data object (slug in app.app or app.appSlug)
 * @returns {string} - Form data endpoint URL
 */
export const getFormDataEndpoint = (app) => {
  const configured = app?.formDataEndpoint;
  if (configured && String(configured).trim() !== '') {
    return String(configured).trim();
  }
  const slug = app?.app || app?.appSlug;
  if (slug) {
    return `${FORMDATA_ENDPOINT_FALLBACK_BASE}/${slug}`;
  }
  return '';
};

/**
 * Get app data by slug
 * @param {string} appSlug - The app slug (e.g., "slack", "gmail")
 * @returns {Object|null} - App data object or null if not found
 */
export const getAppBySlug = (appSlug) => {
  return appsData.find(app => app.app === appSlug) || null;
};

/**
 * Convert an app slug to a readable display name.
 * @param {string} slug
 * @returns {string}
 */
export const slugToDisplayName = (slug) => {
  if (!slug) return 'App';
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Truncate text for meta descriptions.
 * @param {string} text
 * @param {number} maxLen
 * @returns {string}
 */
export const truncateDescription = (text, maxLen = 160) => {
  const cleaned = String(text || '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  if (cleaned.length <= maxLen) return cleaned;
  return `${cleaned.slice(0, maxLen - 1).trim()}…`;
};

/**
 * Normalize a raw seo.json record into the shape used by integration pages.
 * @param {Object|null} rawApp
 * @returns {Object|null}
 */
export const normalizeAppRecord = (rawApp) => {
  if (!rawApp?.app) return null;
  const status = rawApp.status || rawApp.publishStatus || null;
  const category = rawApp.category || rawApp.categoryTitle || 'Popular';
  return {
    app: rawApp.app,
    name: rawApp.name || slugToDisplayName(rawApp.app),
    icon: rawApp.icon || '',
    description: rawApp.description || '',
    category,
    categoryTitle: rawApp.categoryTitle || rawApp.category || 'Popular',
    group: rawApp.group || null,
    homepage: rawApp.homepage || null,
    formDataEndpoint: getFormDataEndpoint(rawApp),
    status,
    publishStatus: rawApp.publishStatus || rawApp.status || null,
  };
};

/**
 * Get display-ready app data from seo.json, with slug-based fallback when missing.
 * @param {string} appSlug
 * @returns {Object|null}
 */
export const getAppDisplayData = (appSlug) => {
  const normalized = normalizeAppRecord(getAppBySlug(appSlug));
  if (normalized) return normalized;
  if (!appSlug) return null;
  return {
    app: appSlug,
    name: slugToDisplayName(appSlug),
    icon: '',
    description: '',
    category: 'App',
    categoryTitle: 'App',
    group: null,
    homepage: null,
    formDataEndpoint: getFormDataEndpoint({ app: appSlug }),
    status: null,
    publishStatus: null,
  };
};

/**
 * SEO metadata for single-app integration pages.
 * @param {string} appSlug
 * @returns {{ appName: string, title: string, description: string, image: string|undefined, breadcrumbName: string, app: Object|null }}
 */
export const getSingleAppPageMeta = (appSlug) => {
  const app = getAppDisplayData(appSlug);
  const appName = app?.name || slugToDisplayName(appSlug);
  const title = `${appName} Integrations | Workload Automation`;
  const description = app?.description
    ? truncateDescription(app.description, 160)
    : `Describe the work with ${appName}. Workload automates outcomes across thousands of apps you already use—builds it, runs it, and keeps it running.`;
  const image = app?.icon || undefined;

  return {
    app,
    appName,
    title,
    description,
    image,
    breadcrumbName: `${appName} Integrations`,
  };
};

/**
 * SEO metadata for two-app integration pages.
 * @param {string} sourceSlug
 * @param {string} targetSlug
 * @returns {{ sourceApp: Object|null, targetApp: Object|null, sourceAppName: string, targetAppName: string, title: string, description: string, image: string|undefined, breadcrumbName: string }}
 */
export const getTwoAppPageMeta = (sourceSlug, targetSlug) => {
  const sourceApp = getAppDisplayData(sourceSlug);
  const targetApp = getAppDisplayData(targetSlug);
  const blocked = new Set(['integrations', 'integration', 'pair', 'api', 'apps', 'app']);
  const safeName = (app, slug) => {
    const name = (app?.name || '').trim();
    if (name && !blocked.has(name.toLowerCase())) return name;
    if (slug && !blocked.has(String(slug).toLowerCase())) return slugToDisplayName(slug);
    return 'App';
  };
  const sourceAppName = safeName(sourceApp, sourceSlug);
  const targetAppName = safeName(targetApp, targetSlug);
  const title = `${sourceAppName} + ${targetAppName} Integration | AI Automation | Workload`;
  const description = truncateDescription(
    `Connect ${sourceAppName} and ${targetAppName} with Workload. Describe what you want the apps to do together and build a monitored AI automation without manually wiring every step.`,
    160
  );
  const image = sourceApp?.icon || targetApp?.icon || undefined;

  return {
    sourceApp,
    targetApp,
    sourceAppName,
    targetAppName,
    title,
    description,
    image,
    breadcrumbName: `${sourceAppName} + ${targetAppName} Integration`,
  };
};

/** Deduplicate apps array by app slug (app.app). API/cache may return the same app
 * in multiple categories, which causes duplicate results in the "Search for an app" dropdown.
 * @param {Object[]} apps - Array of app objects
 * @returns {Object[]} - Array with one entry per unique app slug
 */
export const dedupeAppsBySlug = (apps) => {
  if (!Array.isArray(apps)) return [];
  const seen = new Set();
  return apps.filter((app) => {
    const slug = app?.app;
    if (!slug || seen.has(slug)) return false;
    seen.add(slug);
    return true;
  });
};

/**
 * Get multiple apps by their slugs
 * @param {string[]} appSlugs - Array of app slugs
 * @returns {Object[]} - Array of app data objects in the format expected by components
 */
export const getAppsBySlugs = (appSlugs) => {
  return appSlugs
    .map(slug => getAppBySlug(slug))
    .filter(Boolean)
    .map(app => normalizeAppRecord(app))
    .filter(Boolean);
};

/**
 * Get all apps data
 * @returns {Object[]} - Array of all app data objects in the format expected by components
 */
export const getAllApps = () => {
  return appsData
    .map(app => normalizeAppRecord(app))
    .filter(Boolean);
};

export default appsData;
